import { useEffect, useState } from 'react';
import { useHabitStore } from '../../../stores/habitStore';
import { useSupplementStore } from '../../../stores/supplementStore';
import { toLocalYMD } from '../../../utils/hydration';
import { linkedSupplementsHabit, syncSupplementsHabit } from '../../../utils/supplementsLink';

/**
 * ⭐ Keeps the supplements habit's intervals in step with the stack (owner, 2026-10-23 — step 6
 * of the Supplements pass, the analogue of EatingHabitSync): a supplement added, paused, ended or
 * moved to another time of day — the habit's intervals and daily target change from that day
 * (`syncSupplementsHabit`). Mounted once at the app root. EatingHabitSync's safety rules, all:
 * ⚠️ Writes only once BOTH stores hold the SAME account's saved state: habits loaded for it and
 * its supplement list loaded without error (`initializedUserId`, not `loadFailed`). Before that
 * the supplement store holds the default list — a write from it would be another account's, or
 * nobody's, intervals.
 * ⚠️ Idempotent: a habit already in step returns no patch, so its own update can't loop.
 * The day is re-checked every minute, so an item that ends at midnight is followed.
 * ⚠️⚠️ NEVER brings a deleted habit back: it skips a habit the cloud hasn't confirmed
 * (`unsyncedIds` — its own insert comes first), decides on this tab's copy but APPLIES the change
 * to the cloud's habit, and a write to a habit deleted elsewhere is an UPDATE that finds no row,
 * which drops the habit here (habitStore `writeHabitRow`).
 */
export function SupplementsHabitSync() {
  const habits = useHabitStore(s => s.habits);
  const unsyncedIds = useHabitStore(s => s.unsyncedIds);
  const habitsUser = useHabitStore(s => s.initializedUserId);
  const stackUser = useSupplementStore(s => (s.isInitialized && !s.loadFailed ? s.initializedUserId : null));
  // Both loaded — and for the SAME account (an account switch reloads them one after the other).
  const ready = habitsUser !== null && habitsUser === stackUser;
  const stack = useSupplementStore(s => s.stack);
  const dayTimes = useSupplementStore(s => s.dayTimes);
  const [today, setToday] = useState(() => toLocalYMD(new Date()));

  useEffect(() => {
    const t = window.setInterval(() => {
      const now = toLocalYMD(new Date());
      setToday(d => (d === now ? d : now));
    }, 60_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!ready) return;
    const h = linkedSupplementsHabit(habits);
    if (!h || unsyncedIds.includes(h.id)) return; // not in the cloud yet: an automatic write never creates it
    // ⚠️ Decided on this tab's copy, APPLIED to the cloud's habit (its ticks are moved with the
    // intervals — from this copy they'd be a stale copy): null there = already in step.
    if (syncSupplementsHabit(h, stack, dayTimes, today)) {
      useHabitStore.getState().updateHabit(h.id, (cloud) => syncSupplementsHabit(cloud, stack, dayTimes, today));
    }
  }, [habits, unsyncedIds, ready, stack, dayTimes, today]);

  return null;
}
